import React, { useState, useEffect } from 'react';
import { getAccountAnalytics } from "../api/analyticsApi";
import { Line, Bar } from 'react-chartjs-2';
import { buyCoin, sellCoin } from "../api/tradeApi";
import CountUp from 'react-countup';
import { metalThemes, DEFAULT_THEME } from '../metalThemes';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const username = localStorage.getItem("spectraUser") || "guest";
  const chartsKey = `dashboardCoinCharts_${username}`;

  // --- All State ---
  const [investAmount, setInvestAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [transactions, setTransactions] = useState(3);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [activeChart, setActiveChart] = useState('equity');
  const [activeChartMap, setActiveChartMap] = useState({});
  const [showCoinValue, setShowCoinValue] = useState(false);
  const [showCoinValueMap, setShowCoinValueMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartRange, setChartRange] = useState('1Y');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCoinExpanded, setIsCoinExpanded] = useState(false);
  const [account, setAccount] = useState({ cash: 0, equity: 0, invested: 0 });
  const [tradeRefreshKey, setTradeRefreshKey] = useState(0);
  const [confirmPending, setConfirmPending] = useState(null);
  const [coinCharts, setCoinCharts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`dashboardCoinCharts_${localStorage.getItem("spectraUser") || "guest"}`)) || []; }
    catch { return []; }
  });

  const cardHeight = showCoinValue ? 520 : 350;
  const accountBalance = account.cash;
  const totalInvestment = account.invested;
  const initialInvestment = 10000;

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = parts.length > 1 ? `${intPart}.${parts[1].slice(0, 2)}` : intPart;
    setDisplayAmount(formatted);
    setInvestAmount(raw);
  };

  const rangeToParams = (range) => {
    switch (range) {
      case '1D': return { period: '1D', timeframe: '15Min' };
      case '1M': return { period: '1M', timeframe: '1D' };
      case '3M': return { period: '3M', timeframe: '1D' };
      case '1Y': return { period: '1A', timeframe: '1D' };
      default:   return { period: '1A', timeframe: '1D' };
    }
  };

  // --- All useEffects ---
  useEffect(() => {
    async function loadAccount() {
      try {
        const data = await getAccountAnalytics();
        setAccount({
          cash: Number(data.cash),
          equity: Number(data.equity || data.portfolio_value),
          invested: Number(data.equity || data.portfolio_value) - Number(data.cash),
        });
      } catch (err) {
        console.error("Failed to load account", err);
      }
    }
    const onPortfolioUpdated = () => {
      loadAccount();
      setTradeRefreshKey(k => k + 1);
    };
    loadAccount();
    window.addEventListener("portfolioUpdated", onPortfolioUpdated);
    return () => window.removeEventListener("portfolioUpdated", onPortfolioUpdated);
  }, []);

  useEffect(() => {
    const onAdded = () => {
      try { setCoinCharts(JSON.parse(localStorage.getItem(chartsKey)) || []); }
      catch {}
    };
    window.addEventListener("coinChartAdded", onAdded);
    return () => window.removeEventListener("coinChartAdded", onAdded);
  }, [chartsKey]);

  useEffect(() => {
    if (coinCharts.length === 0) return;
    const { period, timeframe } = rangeToParams(chartRange);
    const token = localStorage.getItem("spectraToken") || "";
    Promise.all(
      coinCharts.map(chart =>
        fetch(`${process.env.REACT_APP_API_URL}/api/portfolio?account=${encodeURIComponent(chart.account)}&period=${period}&timeframe=${timeframe}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r.json())
          .then(json => ({
            ...chart,
            timestamps: json.timestamps || [],
            closes: (json.equity || []).filter(v => v !== null),
            duration: chartRange,
          }))
          .catch(() => chart)
      )
    ).then(updated => {
      setCoinCharts(updated);
      localStorage.setItem(chartsKey, JSON.stringify(updated));
    });
  }, [chartRange, chartsKey, tradeRefreshKey]);


  // --- Equity Chart (placeholder defaults, will be replaced by API data) ---
 
    
     const fullEquityChartData = {
  '1Y': {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',],
    datasets: [{
      label: 'Investment Value ($)',
      data: [500, 700, 800, 750, 900, 1200, 1100],
      borderColor: '#ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.2)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
    }],
  },
  '3M': {
    labels: ['May', 'Jun', 'Jul'],
    datasets: [{
      label: 'Investment Value ($)',
      data: [900, 1200, 1100],
      borderColor: '#ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.2)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
    }],
  },
  '1M': {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Investment Value ($)',
      data: [1050, 1080, 1100, 1125],
      borderColor: '#ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.2)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
    }],
  },
  '1D': {
    labels: ['9AM', '12PM', '3PM', '6PM'],
    datasets: [{
      label: 'Investment Value ($)',
      data: [1100, 1120, 1110, 1130],
      borderColor: '#ffd700',
      backgroundColor: 'rgba(255, 215, 0, 0.2)',
      tension: 0.4,
      fill: true,
      pointRadius: 5,
    }],
  },
};

const fullPlChartData = {
  '1Y': {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',],
    datasets: [{
      label: 'P/L %',
      data: [5, 10, -3, 12, 8, 15, 9],
      borderColor: '#00bfff',
      backgroundColor: 'rgba(0, 191, 255, 0.2)',
      tension: 0.3,
      fill: true,
      pointRadius: 5,
    }],
  },
  '3M': {
    labels: ['May', 'Jun', 'Jul'],
    datasets: [{
      label: 'P/L %',
      data: [8, 15, 9],
      borderColor: '#00bfff',
      backgroundColor: 'rgba(0, 191, 255, 0.2)',
      tension: 0.3,
      fill: true,
      pointRadius: 5,
    }],
  },
  '1M': {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'P/L %',
      data: [6, 7, 8, 9],
      borderColor: '#00bfff',
      backgroundColor: 'rgba(0, 191, 255, 0.2)',
      tension: 0.3,
      fill: true,
      pointRadius: 5,
    }],
  },
  '1D': {
    labels: ['9AM', '12PM', '3PM', '6PM'],
    datasets: [{
      label: 'P/L %',
      data: [1, 2.5, 1.8, 3.1],
      borderColor: '#00bfff',
      backgroundColor: 'rgba(0, 191, 255, 0.2)',
      tension: 0.3,
      fill: true,
      pointRadius: 5,
    }],
  },
};


  const currentChartData = activeChart === 'equity'
    ? fullEquityChartData[chartRange]
    : fullPlChartData[chartRange];

 
    

 

  // --- Execute a confirmed buy/sell order ---
  const executeConfirmedOrder = async () => {
    const { side, symbol, amount, account } = confirmPending;
    setConfirmPending(null);
    try {
      if (side === 'buy') {
        await buyCoin(symbol, amount, account);
      } else {
        await sellCoin(symbol, amount, account);
      }
      const nt = { type: side.toUpperCase(), symbol, amount, date: new Date().toLocaleString() };
      const ex = JSON.parse(localStorage.getItem("spectraTransactions") || "[]");
      ex.push(nt);
      localStorage.setItem("spectraTransactions", JSON.stringify(ex));
      window.dispatchEvent(new Event("transactionsUpdated"));
      window.dispatchEvent(new Event("portfolioUpdated"));
      alert(`${side === 'buy' ? 'Buy' : 'Sell'} order placed!`);
      setInvestAmount('');
      setDisplayAmount('');
    } catch (err) {
      console.error(err);
    }
  };

  // --- Download Chart ---
    const downloadChart = (chartId, name) => {
    const canvas = document.getElementById(chartId);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `${name}_${timestamp}.png`;
    a.click();
  };

  const coinImageMap = {
    Cu: '/coppercoin.png',
    AI: '/aicoin.png',
    U:  '/uraniumcoin.png',
  };

  const activeCoin = coinCharts.length > 0 ? coinCharts[0] : null;
  const activeCoinTheme = activeCoin ? (metalThemes[activeCoin.symbol] || DEFAULT_THEME) : DEFAULT_THEME;

  const chartValues = activeCoin
    ? activeCoin.closes
    : (currentChartData?.datasets?.[0]?.data || []);
  const chartHigh = chartValues.length ? Math.max(...chartValues) : null;
  const chartLow  = chartValues.length ? Math.min(...chartValues) : null;
  const fmtVal = (v) => v != null ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
  const activeCoinImage = activeCoin
    ? (coinImageMap[activeCoin.symbol] || activeCoin.image || '/goldcoin.png')
    : '/goldcoin.png';

  return (
  <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
    {/* Dashboard Header */}
    <div style={{ marginBottom: '24px', textAlign: 'left' }}>
      <h2 style={{ margin: 0 }}>Dashboard</h2>
      <p style={{ margin: '4px 0 0', color: '#555' }}>
        Market Analyzer &amp; Investment Overview — track your portfolio,
        watch trends, and plan your next moves with real-time data.
      </p>
    </div>

    {/* Coin + Combined Chart */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '32px' }}>
      {coinCharts.length === 0 ? (
        <section style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
          {/* LEFT: Coin card (empty state) */}
          <div style={{ backgroundColor: '#111', borderRadius: '12px', width: '35%', height: showCoinValue ? '520px' : '350px', minHeight: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px', transition: 'height 200ms ease', overflow: 'hidden' }}>
            {/* Content group — centers image/text in available space */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', paddingBottom: showCoinValue ? '12px' : '0' }}>
              <img src={activeCoinImage} alt="Spectra Coin" style={{ marginTop: '12px', maxWidth: '75%', height: '70%', objectFit: 'contain', borderRadius: showCoinValue ? '0px' : '16px', transition: 'border-radius 0.3s ease-in-out' }} />
              <h3 style={{ color: activeCoinTheme.border, marginTop: '6px' }}>
                {activeCoin ? `${activeCoin.name}` : 'Real-Time Coin Value'}
              </h3>
              {!showCoinValue && (
                <button onClick={() => setShowCoinValue(true)} style={{ marginTop: '4px', padding: '6px 12px', borderRadius: '12px', border: 'none', background: '#00bfff', color: '#fff', cursor: 'pointer' }}>View Coin</button>
              )}
              {showCoinValue && (
                <>
                  <div style={{ marginTop: '6px', color: activeCoinTheme.border, fontWeight: 'bold', fontSize: '16px', display: 'flex', gap: '6px' }}>
                    <span>Your Investment value =</span>
                    <span>$<CountUp end={activeCoin?.closes?.length > 0 ? activeCoin.closes[activeCoin.closes.length - 1] : 0} duration={1.5} decimals={2} separator="," key={`${activeCoin?.symbol}-${tradeRefreshKey}`} /></span>
                  </div>
                  <p style={{ color: '#aaa', fontSize: '12px', textAlign: 'center', margin: '4px 0' }}>Amount you wish to invest in dollars below</p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <span style={{ position: 'absolute', left: '8px', top: '45%', transform: 'translateY(-50%)', color: '#333', fontWeight: 'bold', pointerEvents: 'none' }}>$</span>
                    <input type="text" inputMode="decimal" value={displayAmount} onChange={handleAmountChange} placeholder="0.00" style={{ padding: '8px 8px 14px 20px', borderRadius: '0px' }} />
                  </div>
                </>
              )}
            </div>
            {/* Buttons — pinned to bottom */}
            {showCoinValue && (
              <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                <button onClick={() => { if (!investAmount) return alert("Enter amount"); setConfirmPending({ side: 'buy', symbol: activeCoin?.symbol, amount: investAmount, account: activeCoin?.account, coinName: activeCoin?.name || 'Coin' }); }} style={{ ...buttonStyle, background: "#28a745", color: "#fff" }}>Buy</button>
                <button onClick={() => { if (!investAmount) return alert("Enter amount"); setConfirmPending({ side: 'sell', symbol: activeCoin?.symbol, amount: investAmount, account: activeCoin?.account, coinName: activeCoin?.name || 'Coin' }); }} style={{ ...buttonStyle, background: "#dc3545", color: "#fff" }}>Sell</button>
                <button onClick={() => setShowCoinValue(false)} style={{ ...buttonStyle, background: '#00bfff', color: '#fff' }}>Hide Value</button>
              </div>
            )}
          </div>



            {/* RIGHT: Chart */}
      <div
        style={{
          width: '65%',
          background: '#fff',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          height: `${cardHeight}px`,
          overflow: 'hidden',
          transition: 'height 200ms ease',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ color: '#ffd700' }}>
            {coinCharts.length === 0 && activeChart === 'equity' ? 'Gold Coin' : ''}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
          <button
  onClick={() => setActiveChart('equity')}
  style={{
    ...buttonStyle,
    padding: '4px 10px',
    fontSize: '11px',
    backgroundColor: activeChart === 'equity' ? '#ddd' : '#eee',
    color: '#333',
    border: activeChart === 'equity' ? '2px solid #999' : '1px solid #ccc',
  }}
     >
       Equity
    </button>

            <button
                  onClick={() => setActiveChart('pl')}
                    style={{
                     ...buttonStyle,
                     padding: '4px 10px',
                     fontSize: '11px',
                     backgroundColor: activeChart === 'pl' ? '#00bfff' : '#eee',
                     color: activeChart === 'pl' ? '#fff' : '#333',
                     border: activeChart === 'pl' ? '2px solid #00bfff' : '1px solid #ccc',
               }}
    >
      P/L %
     </button>

          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {coinCharts.length === 0 ? (
            <Line
              id="combinedChart"
              data={currentChartData}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          ) : (
            coinCharts.map((chart) => {
              const theme = metalThemes[chart.symbol] || DEFAULT_THEME;
              const labels = chart.timestamps.map(t => new Date(t).toLocaleDateString());
              const base = chart.closes[0] || 1;
              const plData = chart.closes.map(v =>
                parseFloat((((v - base) / base) * 100).toFixed(2))
              );
              const barColors = plData.map(v =>
                v >= 0 ? 'rgba(0,191,255,0.7)' : 'rgba(220,53,69,0.7)'
              );
              const barBorders = plData.map(v =>
                v >= 0 ? '#00bfff' : '#dc3545'
              );
              return (
              <div key={`${chart.symbol}-${activeChart}`} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: theme.border, fontWeight: 'bold', fontSize: '13px' }}>
                    {chart.name} ({chart.symbol}) — {chart.duration}
                  </span>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  {activeChart === 'equity' ? (
                    <Line
                      data={{
                        labels,
                        datasets: [{
                          label: `${chart.symbol} Value`,
                          data: chart.closes,
                          borderColor: theme.border,
                          backgroundColor: theme.background,
                          tension: 0.4,
                          fill: true,
                          pointRadius: 3,
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { labels: { color: theme.border } } },
                      }}
                    />
                  ) : (
                    <Bar
                      data={{
                        labels,
                        datasets: [{
                          label: 'P/L %',
                          data: plData,
                          backgroundColor: barColors,
                          borderColor: barBorders,
                          borderWidth: 1,
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { labels: { color: theme.border } } },
                        scales: {
                          y: {
                            ticks: { callback: v => `${v}%` },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </div>
              );
            })
          )}
        </div>

        {showCoinValue && (
          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          


                 <div style={{ position: 'relative' }}>
  <button
    onClick={() => setIsDropdownOpen(prev => !prev)}
    style={{
      ...buttonStyle,
      border: '1px solid #999',
      background: '#eee',
    }}
  >
    {chartRange} ▼
  </button>

  {isDropdownOpen && (
    <div
      style={{
        position: 'absolute',
        bottom: '110%',   // ⬅ opens UP
        left: 0,
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: '6px',
        zIndex: 9999,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden',
      }}
    >
      {['1Y', '3M', '1M', '1D'].map(range => (
        <div
          key={range}
          onClick={() => {
            setChartRange(range);
            setIsDropdownOpen(false);
          }}
          style={{
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            background: chartRange === range ? '#00bfff' : '#fff',
            color: chartRange === range ? '#fff' : '#333',
          }}
        >
   {range}
        </div>
      ))}
   </div>
         )}
        </div>


            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 10px', borderRadius: '8px', background: '#eee', border: '1px solid #ccc', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.4' }}>
              <span style={{ color: '#28a745' }}>▲ High</span>
              <span style={{ color: '#333' }}>{fmtVal(chartHigh)}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 10px', borderRadius: '8px', background: '#eee', border: '1px solid #ccc', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.4' }}>
              <span style={{ color: '#dc3545' }}>▼ Low</span>
              <span style={{ color: '#333' }}>{fmtVal(chartLow)}</span>
            </div>
            {activeCoin && (
              <button
                onClick={() => {
                  const updated = coinCharts.filter(c => c.symbol !== activeCoin.symbol);
                  localStorage.setItem(chartsKey, JSON.stringify(updated));
                  setCoinCharts(updated);
                }}
                style={{ ...buttonStyle, marginLeft: 'auto', backgroundColor: '#eee', color: '#333', border: '1px solid #ccc' }}
              >
                Remove
              </button>
            )}
            <button
              onClick={() => downloadChart('combinedChart', activeChart)}
              style={{
                ...buttonStyle,
                marginLeft: activeCoin ? '0' : 'auto',
                backgroundColor: '#00bfff',
                color: '#fff',
              }}
            >
              Download Chart
            </button>

          </div>
        )}
      </div>
        </section>
      ) : (
        coinCharts.map((coin) => {
          const coinShowValue = showCoinValueMap[coin.symbol] || false;
          const coinCardHeight = coinShowValue ? 520 : 350;
          const coinTheme = metalThemes[coin.symbol] || DEFAULT_THEME;
          const coinImage = coinImageMap[coin.symbol] || coin.image || '/goldcoin.png';
          const coinValues = coin.closes;
          const coinHigh = coinValues.length ? Math.max(...coinValues) : null;
          const coinLow = coinValues.length ? Math.min(...coinValues) : null;
          const labels = coin.timestamps.map(t => new Date(t).toLocaleDateString());
          const base = coin.closes[0] || 1;
          const plData = coin.closes.map(v => parseFloat((((v - base) / base) * 100).toFixed(2)));
          const barColors = plData.map(v => v >= 0 ? 'rgba(0,191,255,0.7)' : 'rgba(220,53,69,0.7)');
          const barBorders = plData.map(v => v >= 0 ? '#00bfff' : '#dc3545');
          const chartId = `coinChart-${coin.symbol}`;
          const coinActiveChart = activeChartMap[coin.symbol] || 'equity';
          return (
            <section key={coin.symbol} style={{ display: 'flex', gap: '24px', alignItems: 'stretch' }}>
              {/* LEFT: Coin card */}
              <div style={{ backgroundColor: '#111', borderRadius: '12px', width: '35%', height: coinShowValue ? '520px' : '350px', minHeight: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px', transition: 'height 200ms ease', overflow: 'hidden' }}>
                {/* Content group — centers image/text in available space */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', paddingBottom: coinShowValue ? '12px' : '0' }}>
                  <img src={coinImage} alt={coin.name} style={{ marginTop: '12px', maxWidth: '75%', height: '70%', objectFit: 'contain', borderRadius: coinShowValue ? '0px' : '16px', transition: 'border-radius 0.3s ease-in-out' }} />
                  <h3 style={{ color: coinTheme.border, marginTop: '6px' }}>{coin.name}</h3>
                  {!coinShowValue && (
                    <button onClick={() => setShowCoinValueMap(prev => ({ ...prev, [coin.symbol]: true }))} style={{ marginTop: '4px', padding: '6px 12px', borderRadius: '12px', border: 'none', background: '#00bfff', color: '#fff', cursor: 'pointer' }}>View Coin</button>
                  )}
                  {coinShowValue && (
                    <>
                      <div style={{ marginTop: '6px', color: coinTheme.border, fontWeight: 'bold', fontSize: '16px', display: 'flex', gap: '6px' }}>
                        <span>Your Investment value =</span>
                        <span>$<CountUp end={coin.closes?.length > 0 ? coin.closes[coin.closes.length - 1] : 0} duration={1.5} decimals={2} separator="," key={`${coin.symbol}-${tradeRefreshKey}`} /></span>
                      </div>
                      <p style={{ color: '#aaa', fontSize: '12px', textAlign: 'center', margin: '4px 0' }}>Amount you wish to invest in dollars below</p>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <span style={{ position: 'absolute', left: '8px', top: '45%', transform: 'translateY(-50%)', color: '#333', fontWeight: 'bold', pointerEvents: 'none' }}>$</span>
                        <input type="text" inputMode="decimal" value={displayAmount} onChange={handleAmountChange} placeholder="0.00" style={{ padding: '8px 8px 14px 20px', borderRadius: '0px' }} />
                      </div>
                    </>
                  )}
                </div>
                {/* Buttons — pinned to bottom */}
                {coinShowValue && (
                  <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                    <button onClick={() => { if (!investAmount) return alert("Enter amount"); setConfirmPending({ side: 'buy', symbol: coin.symbol, amount: investAmount, account: coin.account, coinName: coin.name }); }} style={{ ...buttonStyle, background: "#28a745", color: "#fff" }}>Buy</button>
                    <button onClick={() => { if (!investAmount) return alert("Enter amount"); setConfirmPending({ side: 'sell', symbol: coin.symbol, amount: investAmount, account: coin.account, coinName: coin.name }); }} style={{ ...buttonStyle, background: "#dc3545", color: "#fff" }}>Sell</button>
                    <button onClick={() => setShowCoinValueMap(prev => ({ ...prev, [coin.symbol]: false }))} style={{ ...buttonStyle, background: '#00bfff', color: '#fff' }}>Hide Value</button>
                  </div>
                )}
              </div>
              {/* RIGHT: Chart */}
              <div style={{ width: '65%', background: '#fff', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', height: `${coinCardHeight}px`, overflow: 'hidden', transition: 'height 200ms ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <h3 style={{ color: '#ffd700' }}></h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setActiveChartMap(prev => ({ ...prev, [coin.symbol]: 'equity' }))} style={{ ...buttonStyle, padding: '4px 10px', fontSize: '11px', backgroundColor: coinActiveChart === 'equity' ? '#ddd' : '#eee', color: '#333', border: coinActiveChart === 'equity' ? '2px solid #999' : '1px solid #ccc' }}>Equity</button>
                    <button onClick={() => setActiveChartMap(prev => ({ ...prev, [coin.symbol]: 'pl' }))} style={{ ...buttonStyle, padding: '4px 10px', fontSize: '11px', backgroundColor: coinActiveChart === 'pl' ? '#00bfff' : '#eee', color: coinActiveChart === 'pl' ? '#fff' : '#333', border: coinActiveChart === 'pl' ? '2px solid #00bfff' : '1px solid #ccc' }}>P/L %</button>
                  </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: coinTheme.border, fontWeight: 'bold', fontSize: '13px' }}>{coin.name} ({coin.symbol}) — {coin.duration}</span>
                  </div>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    {coinActiveChart === 'equity' ? (
                      <Line id={chartId} data={{ labels, datasets: [{ label: `${coin.symbol} Value`, data: coin.closes, borderColor: coinTheme.border, backgroundColor: coinTheme.background, tension: 0.4, fill: true, pointRadius: 3 }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { color: coinTheme.border } } } }} />
                    ) : (
                      <Bar id={chartId} data={{ labels, datasets: [{ label: 'P/L %', data: plData, backgroundColor: barColors, borderColor: barBorders, borderWidth: 1 }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { color: coinTheme.border } } }, scales: { y: { ticks: { callback: v => `${v}%` } } } }} />
                    )}
                  </div>
                </div>
                {coinShowValue && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                    <div style={{ position: 'relative' }}>
                      <button onClick={() => setIsDropdownOpen(prev => !prev)} style={{ ...buttonStyle, border: '1px solid #999', background: '#eee' }}>{chartRange} ▼</button>
                      {isDropdownOpen && (
                        <div style={{ position: 'absolute', bottom: '110%', left: 0, background: '#fff', border: '1px solid #ccc', borderRadius: '6px', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                          {['1Y', '3M', '1M', '1D'].map(range => (
                            <div key={range} onClick={() => { setChartRange(range); setIsDropdownOpen(false); }} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', background: chartRange === range ? '#00bfff' : '#fff', color: chartRange === range ? '#fff' : '#333' }}>{range}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 10px', borderRadius: '8px', background: '#eee', border: '1px solid #ccc', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.4' }}>
                      <span style={{ color: '#28a745' }}>▲ High</span>
                      <span style={{ color: '#333' }}>{fmtVal(coinHigh)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 10px', borderRadius: '8px', background: '#eee', border: '1px solid #ccc', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.4' }}>
                      <span style={{ color: '#dc3545' }}>▼ Low</span>
                      <span style={{ color: '#333' }}>{fmtVal(coinLow)}</span>
                    </div>
                    <button onClick={() => { const updated = coinCharts.filter(c => c.symbol !== coin.symbol); localStorage.setItem(chartsKey, JSON.stringify(updated)); setCoinCharts(updated); }} style={{ ...buttonStyle, marginLeft: 'auto', backgroundColor: '#eee', color: '#333', border: '1px solid #ccc' }}>Remove</button>
                    <button onClick={() => downloadChart(chartId, coinActiveChart)} style={{ ...buttonStyle, backgroundColor: '#00bfff', color: '#fff' }}>Download Chart</button>
                  </div>
                )}
              </div>
            </section>
          );
        })
      )}
    </div>

    {confirmPending && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
        <div style={{ background: '#111', color: '#fff', borderRadius: '12px', padding: '28px 32px', minWidth: '300px', boxShadow: '0 8px 32px rgba(0,0,0,0.85)', textAlign: 'center' }}>
          <h3 style={{ marginTop: 0, color: confirmPending.side === 'buy' ? '#28a745' : '#dc3545' }}>
            Confirm {confirmPending.side === 'buy' ? 'Buy' : 'Sell'}
          </h3>
          <p style={{ fontSize: '15px', marginBottom: '24px' }}>
            {confirmPending.side === 'buy' ? 'Buy' : 'Sell'} <strong>${Number(confirmPending.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> of <strong>{confirmPending.coinName}</strong>?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button onClick={executeConfirmedOrder} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', background: confirmPending.side === 'buy' ? '#28a745' : '#dc3545', color: '#fff' }}>
              Confirm
            </button>
            <button onClick={() => setConfirmPending(null)} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', background: '#555', color: '#fff' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
       );
         }



     const buttonStyle = {
         padding: '10px 16px',
         borderRadius: '8px',
         border: 'none',
         fontWeight: 'bold',
        cursor: 'pointer',
     };
